// =============================================================================
// PPIExcecoes — quem foi AUTORIZADO a participar sem ter o tempo mínimo no cargo
// =============================================================================
// Demanda de 2026-08-14: a GN da SVV e o Consultor pediram para convidar ao PPI
// funcionárias com menos de 5 meses no cargo. A alternativa seria baixar a régua
// para a rede inteira por causa de uma pessoa.
//
// ⚠️ A exceção dispensa **SÓ o tempo de cargo**. Recorte (loja/centro/cargo) e a
// régua de saída (aviso prévio, demissão…) continuam valendo — a regra inteira
// mora no backend, em `ppi-recorte.montarElegibilidade`. Esta tela não decide
// nada: ela cadastra o pedido e mostra quem já está autorizado.
//
// ⚠️ Autorizar NÃO envia mensagem. Convidar é o botão "Convidar novos elegíveis",
// que é onde o número aparece e o gasto é confirmado. Disparar WhatsApp pago como
// efeito colateral de um cadastro seria a pior forma de mandar mensagem.

import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Trash2, Search, AlertTriangle } from 'lucide-react';
import { CORES, BadgeStatus, Botao, estilos } from './ppiUI';

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
});

function PPIExcecoes({ idProcesso, onMudou }) {
    const [lista, setLista] = useState([]);
    const [aberto, setAberto] = useState(false);       // o formulário de autorizar
    const [termo, setTermo] = useState('');
    const [resultados, setResultados] = useState([]);
    const [escolhido, setEscolhido] = useState(null);
    const [motivo, setMotivo] = useState('');
    const [erro, setErro] = useState('');
    const [salvando, setSalvando] = useState(false);

    const carregar = useCallback(async () => {
        try {
            const r = await fetch(`/api/treinamento/ppi/processos/${idProcesso}/excecoes`, { headers: authHeaders() });
            if (r.ok) setLista(await r.json());
        } catch (_) { /* silencioso: é carregamento, não ação do usuário */ }
    }, [idProcesso]);

    useEffect(() => { carregar(); }, [carregar]);

    // Busca com atraso: o RH digita o nome inteiro, e uma requisição por tecla
    // varreria a tabela de funcionários a cada letra. O backend já exige 3 letras.
    useEffect(() => {
        if (termo.trim().length < 3) { setResultados([]); return undefined; }
        let cancelado = false;
        const t = setTimeout(async () => {
            try {
                const r = await fetch(
                    `/api/treinamento/ppi/processos/${idProcesso}/buscar-funcionario?termo=${encodeURIComponent(termo.trim())}`,
                    { headers: authHeaders() });
                if (r.ok && !cancelado) setResultados(await r.json());
            } catch (_) { /* silencioso */ }
        }, 350);
        // A flag de cancelamento evita que uma resposta atrasada sobrescreva a
        // atual — o RH digita rápido, e sem ela a lista "volta no tempo".
        return () => { cancelado = true; clearTimeout(t); };
    }, [termo, idProcesso]);

    const autorizar = async () => {
        if (!escolhido) { setErro('Escolha o funcionário.'); return; }
        if (!motivo.trim()) { setErro('Explique por que esta pessoa é uma exceção.'); return; }
        setSalvando(true); setErro('');
        try {
            const r = await fetch(`/api/treinamento/ppi/processos/${idProcesso}/excecoes`, {
                method: 'POST', headers: authHeaders(),
                body: JSON.stringify({ idFuncionario: escolhido.idFuncionario, motivo: motivo.trim() }),
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.message || 'Erro ao autorizar.');
            setAberto(false); setEscolhido(null); setMotivo(''); setTermo(''); setResultados([]);
            await carregar();
            onMudou?.();          // a prévia de destinatários muda com a exceção nova
        } catch (e) { setErro(e.message); }
        finally { setSalvando(false); }
    };

    const remover = async (exc) => {
        if (!window.confirm(`Remover a autorização de ${exc.NOME}?\n\nEla volta a precisar do tempo mínimo no cargo.`)) return;
        setErro('');
        try {
            const r = await fetch(`/api/treinamento/ppi/excecoes/${exc.ID}`, {
                method: 'DELETE', headers: authHeaders(),
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.message || 'Erro ao remover.');
            await carregar();
            onMudou?.();
        } catch (e) { setErro(e.message); }
    };

    return (
        <div style={{ border: `1px solid ${CORES.borda}`, borderRadius: '12px', padding: '18px', margin: '24px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                    <h4 style={{ fontSize: '16px', fontWeight: 800, color: CORES.texto, margin: 0 }}>
                        Exceções ao tempo mínimo no cargo
                    </h4>
                    <p style={{ fontSize: '13px', color: CORES.textoFraco, margin: '4px 0 0 0' }}>
                        Quem entra aqui participa <strong>sem ter o tempo mínimo no cargo</strong> — e só isso:
                        continua precisando estar dentro do público-alvo e fora de aviso prévio.
                    </p>
                </div>
                {!aberto && (
                    <Botao variant="secundario" icone={UserPlus} onClick={() => { setAberto(true); setErro(''); }}>
                        Autorizar alguém
                    </Botao>
                )}
            </div>

            {erro && <p style={{ color: '#be123c', fontSize: '13px', marginTop: '10px' }}>{erro}</p>}

            {aberto && (
                <div style={{ marginTop: '14px', padding: '14px', backgroundColor: CORES.bgSuave, borderRadius: '10px' }}>
                    <label style={estilos.rotuloFiltro}>Funcionário (nome ou CPF)</label>
                    <div style={{ position: 'relative' }}>
                        <Search size={15} style={{ position: 'absolute', left: '10px', top: '12px', color: CORES.textoFraco }} />
                        <input
                            autoFocus
                            style={{ ...estilos.input, paddingLeft: '32px' }}
                            placeholder="Digite ao menos 3 letras…"
                            value={escolhido ? escolhido.nome : termo}
                            onChange={(e) => { setEscolhido(null); setTermo(e.target.value); }}
                        />
                    </div>

                    {!escolhido && resultados.length > 0 && (
                        <div style={{ marginTop: '8px', maxHeight: '190px', overflowY: 'auto', border: `1px solid ${CORES.borda}`, borderRadius: '8px', backgroundColor: '#fff' }}>
                            {resultados.map((f) => (
                                <button
                                    key={f.idFuncionario}
                                    onClick={() => { setEscolhido(f); setResultados([]); }}
                                    style={{
                                        display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px',
                                        border: 'none', borderBottom: `1px solid #f1f5f9`, backgroundColor: '#fff',
                                        cursor: 'pointer', fontSize: '13px', color: CORES.texto,
                                    }}>
                                    <strong>{f.nome}</strong>
                                    <span style={{ color: CORES.textoFraco }}> · {f.loja || '—'} · {f.cargo || '—'}</span>
                                    {/* Autorizar quem JÁ é elegível não muda nada e só polui a
                                        lista — então a tela avisa antes, em vez de deixar o RH
                                        descobrir depois que a exceção não fez diferença. */}
                                    {f.temTempoDeCargo === true && (
                                        <span style={{ marginLeft: '6px' }}>
                                            <BadgeStatus tom="aprovado">já é elegível</BadgeStatus>
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    {escolhido?.temTempoDeCargo === true && (
                        <p style={{ fontSize: '12px', color: '#b45309', margin: '8px 0 0 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <AlertTriangle size={14} />
                            {escolhido.nome} já tem o tempo mínimo no cargo — a exceção não muda nada para ele(a).
                        </p>
                    )}

                    <div style={{ marginTop: '12px' }}>
                        <label style={estilos.rotuloFiltro}>
                            Por que esta pessoa é uma exceção? <span style={{ color: '#be123c' }}>*</span>
                        </label>
                        <textarea
                            style={{ ...estilos.input, minHeight: '70px', resize: 'vertical' }}
                            placeholder="Ex.: pedido da GN da SVV e do Consultor — desempenho acima do esperado no período."
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                        />
                        {/* O motivo é obrigatório NO SERVIDOR também. A frase abaixo diz
                            POR QUE ele é pedido — sem isso vira campo chato de preencher
                            e o RH escreve "ok". */}
                        <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '6px 0 0 0' }}>
                            Fica registrado com o seu nome. Daqui a três meses, é isto que explica
                            por que esta pessoa participou sem o tempo mínimo.
                        </p>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                        <Botao onClick={autorizar} disabled={salvando}>
                            {salvando ? 'Autorizando…' : 'Autorizar'}
                        </Botao>
                        <Botao variant="secundario" onClick={() => {
                            setAberto(false); setEscolhido(null); setMotivo(''); setTermo(''); setResultados([]); setErro('');
                        }}>
                            Cancelar
                        </Botao>
                    </div>
                </div>
            )}

            {lista.length > 0 && (
                <div style={{ ...estilos.tabelaWrap, marginTop: '14px' }}>
                    <table style={estilos.tabela}>
                        <thead><tr>
                            <th style={estilos.th}>Funcionário</th>
                            <th style={estilos.th}>Motivo</th>
                            <th style={estilos.th}>Autorizado por</th>
                            <th style={{ ...estilos.th, textAlign: 'center' }}>—</th>
                        </tr></thead>
                        <tbody>
                            {lista.map((e) => (
                                <tr key={e.ID}>
                                    <td style={estilos.td}>
                                        <strong>{e.NOME}</strong>
                                        {Number(e.JA_INSCRITO) === 1 && (
                                            <div style={{ marginTop: '4px' }}>
                                                <BadgeStatus tom="inscrito">já se inscreveu</BadgeStatus>
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ ...estilos.td, color: CORES.textoSec, maxWidth: '340px' }}>{e.MOTIVO}</td>
                                    <td style={{ ...estilos.td, color: CORES.textoFraco, whiteSpace: 'nowrap' }}>
                                        {e.AUTORIZADO_POR || '—'}<br />
                                        <span style={{ fontSize: '12px' }}>{e.DATA}</span>
                                    </td>
                                    <td style={{ ...estilos.td, textAlign: 'center' }}>
                                        {/* Depois da inscrição a autorização vira histórico: apagá-la
                                            deixaria um inscrito sem a justificativa que o trouxe, e
                                            ele continuaria no processo do mesmo jeito. O backend
                                            também recusa (409) — a tela esconde por clareza. */}
                                        {Number(e.JA_INSCRITO) === 1 ? (
                                            <span style={{ fontSize: '12px', color: CORES.textoFraco }}>—</span>
                                        ) : (
                                            <button onClick={() => remover(e)} title="Remover autorização"
                                                style={{
                                                    display: 'inline-flex', padding: '7px', borderRadius: '8px',
                                                    border: '1px solid #fbb6c4', backgroundColor: '#fff',
                                                    color: '#e11d48', cursor: 'pointer',
                                                }}>
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!lista.length && !aberto && (
                <p style={{ fontSize: '13px', color: CORES.textoFraco, margin: '12px 0 0 0' }}>
                    Ninguém autorizado por exceção neste processo.
                </p>
            )}
        </div>
    );
}

export default PPIExcecoes;
